"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, HardHat, Award, ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const principles = [{
  icon: HardHat,
  title: "20+ years on the tools",
  description: "Led by Pete Wood, bringing two decades of practical scaffolding and direct site management across the South East."
}, {
  icon: ShieldCheck,
  title: "Safety as standard",
  description: "Every structure is built to NASC and CHAS standards with zero compromise on crew or public safety."
}, {
  icon: Award,
  title: "The Adam Story legacy",
  description: "Growing BMF while honouring the foundational standards, personal integrity, and client trust established from day one."
}];
export default function Story() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = {
    duration: shouldReduceMotion ? 0 : 0.45,
    ease: [0.16, 1, 0.3, 1] as const
  };
  const fadeUp = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section id="our-story" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 lg:py-28 border-b border-border">
      <div className="container mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} transition={transitionConfig} variants={fadeUp} className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-0.75 w-10 bg-primary" aria-hidden="true" />
            <span className="text-xs md:text-sm font-bold tracking-wider uppercase text-muted-foreground">
              Our background & principles
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground mb-6">
            Built on frontline experience and family accountability.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            BMF Scaffolding Ltd operates with the technical muscle of a tier-one contractor and the direct care of an independent, family-run team.
          </p>
        </motion.div>

        {/* Narrative & Image Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          
          {/* Narrative Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.1
        }} variants={fadeUp} className="lg:col-span-7 flex flex-col gap-6">
            <div className="space-y-4 text-base md:text-lg text-foreground/90 leading-relaxed font-normal">
              <p>
                BMF Scaffolding Ltd was established to give contractors, developers, and homeowners an uncompromising standard of access engineering. We do not cut corners or chase low-margin shortcuts; we deliver solid, compliant scaffolding that keeps your job running safely and on schedule.
              </p>
              <p>
                Director Pete Wood brings more than 20 years of hands-on scaffolding experience to every project. That operational background shapes how we design tube layouts, supervise on-site erections, and inspect complex commercial frames across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East.
              </p>
              <p className="text-muted-foreground border-l-2 border-primary pl-4 my-4 italic">
                We carry forward the legacy of Adam Story with quiet pride and total dedication to quality, ensuring the respect and trust associated with our name remain at the heart of our everyday site work. Adam's daughters remain part of the BMF story today, and continuing to grow the business is, in part, a way of carrying that legacy forward.
              </p>
              <p>
                Whether erecting an engineered temporary roof, supporting high-output solar panel teams, or providing tidy domestic chimney scaffolding, you deal directly with experienced managers who take personal responsibility for the work.
              </p>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                <Link to="/ContactRequestQuotePage">
                  Discuss a project
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent text-foreground">
                <Link to="/HealthSafetyAccreditationsPage">
                  View our accreditations
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent text-foreground">
                <Link to="/ProjectsCaseStudiesPage">
                  See our projects
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent text-foreground">
                <Link to="/TestimonialsPage">
                  Read testimonials
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Photograph Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.2
        }} variants={fadeUp} className="lg:col-span-5">
            <div className="relative rounded-lg overflow-hidden border border-border bg-card shadow-md">
              <div className="p-6 sm:p-8 bg-card flex items-center justify-center">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/BMF_Scaffolding_Transparent_Logo.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/BMF_Scaffolding_Transparent_Logo.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/BMF_Scaffolding_Transparent_Logo.png-wvc-sizes" alt="BMF Scaffolding transparent logo" loading="lazy" decoding="async" className="w-full aspect-4/3 sm:aspect-16/11 object-contain" />
              </div>
              <div className="p-4 sm:p-5 bg-card border-t border-border">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-sm font-bold text-foreground">Site Leadership & Inspection</h2>
                    <p className="text-xs text-muted-foreground">South East England & Home Counties</p>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-muted rounded text-xs font-semibold text-foreground">
                    <ShieldCheck className="size-3.5 text-primary" />
                    <span>NASC Aligned</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Pillars Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14 pt-12 border-t border-border">
          {principles.map((principle, i) => {
          const IconComponent = principle.icon;
          return <motion.div key={principle.title} data-index={i} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            ...transitionConfig,
            delay: shouldReduceMotion ? 0 : 0.1 * i
          }} variants={fadeUp}>
                <Card className="h-full border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="size-11 rounded-md bg-accent flex items-center justify-center text-foreground mb-4">
                      <IconComponent className="size-5 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">
                      {principle.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {principle.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Direct Contact Banner */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        ...transitionConfig,
        delay: shouldReduceMotion ? 0 : 0.3
      }} variants={fadeUp} className="mt-12 rounded-lg bg-secondary text-secondary-foreground p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border border-border/20">
          <div>
            <h3 className="text-xl md:text-2xl font-bold mb-1 text-secondary-foreground">
              Speak directly with our operational team
            </h3>
            <p className="text-secondary-foreground/80 text-sm md:text-base">
              Call our office Monday to Friday, 8am to 6pm, to arrange a site survey or discuss technical specifications.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
              <Link to="/ContactRequestQuotePage">
                Request a quote
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-transparent text-secondary-foreground border-secondary-foreground/30 hover:bg-secondary-foreground/10">
              <Link to="/ContactRequestQuotePage">
                <Phone className="size-4" />
                Contact us
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}